import React, { type ReactNode, useCallback, useEffect, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { loadEntryPoint } from 'react-relay';

import type { Placement } from '@atlaskit/popper';
import { Popper as ReactPopper } from '@atlaskit/popper';
import Portal from '@atlaskit/portal';
import { Box } from '@atlaskit/primitives/compiled';
import { layers } from '@atlaskit/theme/constants';
import { InternalEntryPointContainer } from '@atlassian/internal-entry-point-container';
import {
	AtlassianRelayEnvironmentProvider,
	useRelayEnvironmentProvider,
} from '@atlassian/relay-environment-provider';

import {
	entryPoint,
	type UserProfileCardRelayTriggerParams,
} from '../network/userProfileCard.entrypoint';
import { getPageTime } from '../utils/performance';

import { CardError } from './components/CardError';
import { CardLoading } from './components/CardLoading';
import { UserProfileCardWithTrigger } from './UserProfileCardWithTrigger';

const ENTRY_POINT_ID = 'user-profile-card-for-mention';

type EntryPointReference = ReturnType<typeof loadEntryPoint<typeof entryPoint>>;

type PopperContentProps = {
	entryPointReference: EntryPointReference;
	entryPointErrorFallback: (props: { error: Error; onClose: () => void }) => React.JSX.Element;
	cloudId: string;
	testId?: string;
};

function PopperContent({
	entryPointReference,
	entryPointErrorFallback,
	cloudId,
	testId,
}: PopperContentProps): React.JSX.Element {
	return (
		<InternalEntryPointContainer
			id={ENTRY_POINT_ID}
			entryPointReference={entryPointReference}
			fallback={<CardLoading />}
			errorFallback={entryPointErrorFallback}
			runtimeProps={{
				cloudId,
				testId,
			}}
		/>
	);
}

export type UserProfileCardForMentionProps = UserProfileCardRelayTriggerParams & {
	/** The `@user` mention to wrap (renderer) or render alongside the card (editor). */
	children: ReactNode;
	/** DOM element the card anchors to. Provide in the editor; omit in the renderer. */
	referenceElement?: HTMLElement;
	/** Where to position the card relative to the mention. Defaults to `'bottom-start'`. */
	placement?: Placement;
	/** Test ID prefix for unit / VR tests. */
	testId?: string;
};

/**
 * Profile card for `@user` mentions.
 *
 * Editor mentions are tricky because the underlying mention DOM is owned by ProseMirror
 * (a non-React framework), so a React-trigger-based card can't attach hover / click handlers
 * to it like it would for any normal mention in the renderer. The editor decides when the card
 * should appear (e.g. while a node is selected) and provides the DOM element to anchor to.
 * Meanwhile the renderer (read-only view) renders mentions as plain React elements, where the
 * standard click / hover trigger UX works fine.
 *
 * Rather than make every consumer wire up two different components for these two surfaces,
 * this component dispatches internally based on whether `referenceElement` is provided:
 *
 * - **Renderer (read-only):** omit `referenceElement`. The card opens on click of `children`.
 * - **Editor / nodeview:** pass `referenceElement`. The card stays open while mounted, anchored to
 *   that element. The editor controls open / close by mounting / unmounting this component.
 */
export function UserProfileCardForMention(props: UserProfileCardForMentionProps): React.JSX.Element {
	return (
		<AtlassianRelayEnvironmentProvider>
			<UserProfileCardForMentionInner {...props} />
		</AtlassianRelayEnvironmentProvider>
	);
}

/** Inner implementation of {@link UserProfileCardForMention}. */
function UserProfileCardForMentionInner({
	userId,
	cloudId,
	orgId,
	viewerId,
	children,
	referenceElement,
	placement = 'bottom-start',
	testId,
}: UserProfileCardForMentionProps): React.JSX.Element {
	const environmentProvider = useRelayEnvironmentProvider();

	const onErrorBoundary = useCallback(() => {
		void getPageTime();
	}, []);

	const entryPointErrorFallback = useCallback(
		(_props: { error: Error; onClose: () => void }) => <CardError testId={testId} />,
		[testId],
	);

	const [entryPointReference, setEntryPointReference] = useState<EntryPointReference | null>(null);

	useEffect(() => {
		if (!referenceElement || !userId || !cloudId || !orgId || !viewerId) {
			return;
		}
		const reference = loadEntryPoint(environmentProvider, entryPoint, {
			userId,
			cloudId,
			orgId,
			viewerId,
		});
		setEntryPointReference(reference);
		return () => {
			reference.dispose();
			setEntryPointReference(null);
		};
	}, [referenceElement, userId, cloudId, orgId, viewerId, environmentProvider]);

	// Both render paths require these identifiers to make a valid API call. Bail out to
	// the plain children (mention chip without a card) when any are missing - the editor
	// path also needs this guard before its popper renders.
	if (!userId || !cloudId || !orgId || !viewerId) {
		return <>{children}</>;
	}

	if (!referenceElement) {
		return (
			<UserProfileCardWithTrigger
				userId={userId}
				cloudId={cloudId}
				orgId={orgId}
				viewerId={viewerId}
				placement={placement}
				testId={testId}
				shouldRenderToParent
			>
				{children}
			</UserProfileCardWithTrigger>
		);
	}

	return (
		<>
			{children}
			<ErrorBoundary onError={onErrorBoundary} fallback={null}>
				<Portal zIndex={layers.modal()}>
					<ReactPopper referenceElement={referenceElement} placement={placement}>
						{({ ref, style }) => (
							<Box
								ref={ref as React.Ref<HTMLDivElement>}
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- positioning controlled by popper
								style={style}
								testId={testId ? `${testId}--mention-popup` : undefined}
							>
								{entryPointReference ? (
									<PopperContent
										entryPointReference={entryPointReference}
										entryPointErrorFallback={entryPointErrorFallback}
										cloudId={cloudId}
										testId={testId}
									/>
								) : (
									<CardLoading />
								)}
							</Box>
						)}
					</ReactPopper>
				</Portal>
			</ErrorBoundary>
		</>
	);
}
